import type { AaiSession, CompanyDashboard, ControlEvent, DepartmentAggregate, OwnerProgram } from '../../types/aai';

/*
 * The one vocabulary for the trainer's console.
 *
 * A trainer delivers the program: they answer what the company asks of them,
 * run the sessions on the calendar, and target the teams whose readiness says
 * they need support first. Every number here is a company or team aggregate —
 * a trainer sees who is onboarded, never how one person scored.
 */

export const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Sessions still ahead, soonest first. A session with no date is not scheduled. */
export const upcomingSessions = (sessions: AaiSession[]): AaiSession[] => {
  const now = Date.now();
  return sessions
    .filter((s) => s.status !== 'completed' && s.scheduled_at && new Date(s.scheduled_at).getTime() >= now)
    .sort((a, b) => new Date(a.scheduled_at || 0).getTime() - new Date(b.scheduled_at || 0).getTime());
};

/** Scheduled inside the next seven days: what the trainer is preparing for now. */
export const thisWeek = (sessions: AaiSession[]): AaiSession[] => {
  const limit = Date.now() + 7 * 86400000;
  return upcomingSessions(sessions).filter((s) => new Date(s.scheduled_at || 0).getTime() <= limit);
};

/** Sessions with a cohort but no date. They are planned and cannot be run yet. */
export const undatedSessions = (sessions: AaiSession[]): AaiSession[] =>
  sessions.filter((s) => s.status !== 'completed' && !s.scheduled_at);

export const daysSince = (iso: string | null | undefined): number => {
  if (!iso) return Number.POSITIVE_INFINITY;
  return (Date.now() - new Date(iso).getTime()) / 86400000;
};

export const teamName = (d: DepartmentAggregate): string => d.department || 'Unassigned';

export const isProtected = (d: DepartmentAggregate): boolean => d.suppressed || Boolean(d.maturity?.suppressed);

/** A team under the midpoint needs first-use support before its work lands. */
export const NEEDS_SUPPORT_BELOW = 50;

/** Below this many onboarded people a team aggregate is not reportable. */
export const MIN_GROUP_SIZE = 4;

export const teamsNeedingSupport = (dashboard: CompanyDashboard | null): DepartmentAggregate[] => {
  if (!dashboard) return [];
  return (dashboard.departments || [])
    .filter((d) => !isProtected(d) && (d.maturity?.maturity_pct ?? 0) < NEEDS_SUPPORT_BELOW)
    .sort((a, b) => (a.maturity?.maturity_pct ?? 0) - (b.maturity?.maturity_pct ?? 0));
};

/** What a team's readiness says, or why it says nothing. */
export const readinessLine = (d: DepartmentAggregate, minGroup = MIN_GROUP_SIZE): string => {
  if (isProtected(d)) return `Protected · ${d.onboarded_count} onboarded · groups under ${minGroup} are not reportable`;
  const pct = d.maturity?.maturity_pct;
  if (pct === null || pct === undefined) return `${d.onboarded_count} onboarded · not scored yet`;
  return `${pct}% readiness · ${d.onboarded_count} onboarded`;
};

/** Who runs this program, said the way the header says it. */
export const programLine = (program: OwnerProgram | null): string => {
  if (!program) return 'No program has been set up for this company yet.';
  const parts = [program.tier_label || 'Program', `run by ${program.manager_label}`];
  if (program.starts_on) parts.push(`started ${new Date(program.starts_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`);
  if (!program.starts_on) parts.push('no start date set');
  return parts.join(' · ');
};

/** The team an action names, for grouping a queue by team. */
export const teamOfEvent = (e: ControlEvent): string => {
  const value = (e.payload || {}).department;
  if (typeof value === 'string' && value) return value;
  return 'Whole company';
};

export const SESSION_TYPE_LABEL: Record<string, string> = {
  coaching: 'Coaching',
  'quick-win': 'Quick Win',
  'deep-dive': 'Deep dive',
};
